import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { IRadioButtonCard } from "@app/directives/_forms/_radio-buttons/radio-button-cards/radio-button-cards.component";
import { ISamlConfiguration, SamlConfigurationStatus, SamlConfigurationVerificationStatus, SamlService } from "@app/services/saml.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import "./saml-configuration.component.scss";

@UntilDestroy()
@Component({
    selector: "saml-configuration",
    template: require("./saml-configuration.component.html")
})
export class SamlConfigurationComponent implements OnInit {

    @Output() onClose = new EventEmitter();

    // ---

    loading = false;
    saving = false;
    verificationCheckLoading = false;

    configuration: ISamlConfiguration = {} as any;
    configurationForm: FormGroup;

    ssoEnforcementSettings: IRadioButtonCard[];
    SamlConfigurationVerificationStatus = SamlConfigurationVerificationStatus;

    constructor(
        private readonly samlService: SamlService,
        private readonly translationService: TranslationService) {
    }

    ngOnInit() {
        this.loadConfigData();
    }

    validateTxtRecord() {
        this.verificationCheckLoading = true;

        this.samlService.validateTXTRecord(this.configuration.Domain).pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.configuration.VerificationStatus = SamlConfigurationVerificationStatus.Verified;
                },
                error: () => this.configuration.VerificationStatus = SamlConfigurationVerificationStatus.Requested,
                complete: () => this.verificationCheckLoading = false
            });
    }

    save() {
        this.saving = true;

        this.configuration.Domain = this.configurationForm.get("Domain").value;
        this.configuration.EntityId = this.configurationForm.get("EntityId").value;
        this.configuration.Endpoint = this.configurationForm.get("Endpoint").value;
        this.configuration.Certificate = this.configurationForm.get("Certificate").value;
        this.configuration.Status = this.configurationForm.get("SSOEnforcementSettings").value;

        this.samlService.saveConfiguration(this.configuration).pipe(untilDestroyed(this))
            .subscribe({
                complete: () => {
                    this.saving = false;
                    this.closeEditor();

                    setTimeout(() => {
                        this.loadConfigData();
                    }, 500);
                }
            });
    }

    closeEditor(): void {
        this.onClose.emit();
    }

    // ---

    private loadConfigData() {
        this.loading = true;

        this.samlService.getConfiguration()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: data => {
                    this.configuration = data;
                },
                complete: () => {
                    this.loading = false;

                    this.configuration = this.configuration || {} as any;

                    this.createFormGroup();
                }
            });
    }

    private createFormGroup() {
        if (this.configurationForm) {
            return;
        }

        this.createRadioButtons();
        const selectedEnforcementSetting = this.ssoEnforcementSettings.find(s => s.value == this.configuration.Status) || this.ssoEnforcementSettings[0];

        this.configurationForm = new FormGroup({
            "Domain": new FormControl(this.configuration.Domain || "", [Validators.required, CustomFormValidators.emptyWhiteSpace]),
            "EntityId": new FormControl(this.configuration.EntityId || "", [Validators.required, CustomFormValidators.emptyWhiteSpace]),
            "Endpoint": new FormControl(this.configuration.Endpoint || "", [Validators.required, CustomFormValidators.emptyWhiteSpace]),
            "Certificate": new FormControl(this.configuration.Certificate || "", [Validators.required, CustomFormValidators.emptyWhiteSpace]),

            "SSOEnforcementSettings": new FormControl(selectedEnforcementSetting, [Validators.required])
        });
    }

    private createRadioButtons() {

        this.ssoEnforcementSettings = [
            { label: this.translationService.t("Common.Disabled"), description: this.translationService.t("SamlConfig.DisabledMessage"), value: SamlConfigurationStatus.Inactive },
            { label: this.translationService.t("SamlConfig.Hybrid"), description: this.translationService.t("SamlConfig.HybridDescription"), value: SamlConfigurationStatus.Testing, disabled: this.configuration.VerificationStatus != SamlConfigurationVerificationStatus.Verified },
            { label: this.translationService.t("SamlConfig.Enforced"), description: this.translationService.t("SamlConfig.EnforcedDescription"), value: SamlConfigurationStatus.Active, disabled: this.configuration.VerificationStatus != SamlConfigurationVerificationStatus.Verified },
        ];
    }
}
